import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import dayjs from "dayjs";

export default function DateFilter({ onChange, value }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [openCustom, setOpenCustom] = useState(false);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const options = ["This week", "This month", "Last 3 months", "Custom"];

  // Calculate date range based on selected option
  const calculateDateRange = (option, customStartDate = null, customEndDate = null) => {
    const today = dayjs();
    let start_date, end_date;

    if (option === "This week") {
      start_date = today.startOf("week").format("YYYY-MM-DD");
      end_date = today.endOf("week").format("YYYY-MM-DD");
    } else if (option === "This month") {
      start_date = today.startOf("month").format("YYYY-MM-DD");
      end_date = today.endOf("month").format("YYYY-MM-DD");
    } else if (option === "Last 3 months") {
      start_date = today.subtract(3, "months").startOf("month").format("YYYY-MM-DD");
      end_date = today.format("YYYY-MM-DD");
    } else if (option === "Custom" && customStartDate && customEndDate) {
      start_date = customStartDate;
      end_date = customEndDate;
    }

    return { label: option, start_date, end_date };
  };

  // Get current range based on value prop
  const currentRange = useMemo(() => {
    if (value && value.label) {
      return value;
    }
    return calculateDateRange("Last 3 months");
  }, [value]);

  const handleSelect = (option) => {
    if (option === "Custom") {
      setOpenCustom(true);
    } else {
      const dateRange = calculateDateRange(option);
      onChange && onChange(dateRange);
    }
    setAnchorEl(null);
  };

  const applyCustomRange = () => {
    if (customStart && customEnd) {
      const dateRange = calculateDateRange("Custom", customStart, customEnd);
      onChange && onChange(dateRange);
      setOpenCustom(false);
      setCustomStart("");
      setCustomEnd("");
    }
  };
 
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        width: "100%",
        marginBottom: "10px",
      }}
    >
      <Button
        variant="outlined"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          width: "fit-content",
          borderRadius: "6px",
          padding: "6px 12px",
          fontSize: "14px",
          borderColor: "#6366F1",
          color: "#111827",
          textTransform: "none",
          whiteSpace: "nowrap",
          minHeight: "32px"
        }}
        endIcon={<CalendarTodayIcon fontSize="small" />}
      >
        {currentRange.label}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {options.map((opt) => (
          <MenuItem key={opt} onClick={() => handleSelect(opt)}>
            {opt}
          </MenuItem>
        ))}
      </Menu>

      <Dialog open={openCustom} onClose={() => setOpenCustom(false)}>
        <DialogTitle>Select Custom Date Range</DialogTitle>

        <DialogContent>
          <TextField
            label="Start Date"
            type="date"
            fullWidth
            sx={{ mt: 2 }}
            InputLabelProps={{ shrink: true }}
            value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
          />
          <TextField
            label="End Date"
            type="date"
            fullWidth
            sx={{ mt: 2 }}
            InputLabelProps={{ shrink: true }}
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenCustom(false)}>Cancel</Button>
          <Button
            variant="contained"
            sx={{ background: "#6366F1" }}
            onClick={applyCustomRange}
          >
            Apply Range
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
